import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '../../store';
import { sortPlansByData } from '../../shared/helpers/helpers';
import { Billing, ListGroupPlansOutput } from "@smash-sdk/billing";
import { Vat as VatSdk, ComputeVatInput, ComputeVatOutput } from "@smash-sdk/vat/ejs/10-2019";
import { AsyncState } from '../../shared/models/async.interface';
import { Captcha as CaptchaSdk, VerifyChallengeOutput } from '@smash-sdk-private/captcha';
import { Plan } from './models/Plan.interface';

interface AvailablePlans extends AsyncState {
    plans: ListGroupPlansOutput['plans']
}

interface SelectedPlan extends AsyncState {
    plan: Plan | null;
}

interface Vat extends AsyncState {
    vat: ComputeVatOutput['vat'] | null;
}

interface Captcha extends AsyncState {
    challenge: VerifyChallengeOutput['challenge'] | null;
}

export interface PlanUpgradeState {
    availablePlans: AvailablePlans;
    selectedPlan: SelectedPlan,
    vat: Vat,
    captcha: Captcha
}

export const fetchPlans = createAsyncThunk(
    'planUpgrade/fetchPlans',
    async (_, thunkApi) => {
        try {
            const billing = new Billing();
            const { plans } = await billing.listGroupPlans({ groupId: 'Smash Api' });
            return { availablePlans: sortPlansByData(plans) };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const choosePlan = createAsyncThunk(
    'planUpgrade/choosePlan',
    async ({ planIdSelected, defaultCurrency }: { planIdSelected: string, defaultCurrency?: string }, thunkApi) => {
        try {
            const { planUpgrade }: any = thunkApi.getState();
            const availablePlans = planUpgrade.availablePlans.plans
                .map((item: AvailablePlans['plans'][0]) => ({ ...item, selected: false }))
                .map((item: AvailablePlans['plans'][0]) => ({
                    ...item,
                    selected: planIdSelected ? !!(item.id === planIdSelected) : !!(item.currency === defaultCurrency && item.name === process.env.REACT_APP_SMASH_PLAN_API_GROUP_ID)
                }));

            const plan = availablePlans.find((plan: any) => plan.selected);
            return { plan, availablePlans: sortPlansByData(availablePlans) };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const computeVat = createAsyncThunk(
    'planUpgrade/computeVat',
    async (input: ComputeVatInput, thunkApi) => {
        try {
            return await new VatSdk().computeVat(input);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const verifyChallenge = createAsyncThunk(
    'planUpgrade/verifyChallenge',
    async (token: string, thunkApi) => {
        try {
            return await new CaptchaSdk().verifyChallenge({ token, action: 'upgrade api plan' });
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)


export const initialState: PlanUpgradeState = {
    availablePlans: {
        plans: [],
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    selectedPlan: {
        plan: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    vat: {
        vat: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    captcha: {
        challenge: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
}

export const planUpgradeSlice = createSlice({
    name: 'planUpgrade',
    initialState,
    reducers: {
        resetPlanUpgrade: (state) => ({
            availablePlans: {
                plans: [],
                isLoading: false,
                isSuccess: false,
                isError: false,
            },
            selectedPlan: {
                plan: null,
                isLoading: false,
                isSuccess: false,
                isError: false,
            },
            vat: {
                vat: null,
                isLoading: false,
                isSuccess: false,
                isError: false,
            },
            captcha: {
                challenge: null,
                isLoading: false,
                isSuccess: false,
                isError: false,
            }
        }),
        resetVat: (state) => {
            state.vat = initialState.vat;
            return state;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPlans.pending, (state) => {
                state.availablePlans.isLoading = true;
                state.availablePlans.isSuccess = false;
                state.availablePlans.isError = false;
            })
            .addCase(fetchPlans.fulfilled, (state, action) => {
                state.availablePlans.isSuccess = true;
                state.availablePlans.isLoading = false;
                state.availablePlans.plans = action.payload.availablePlans;
            })
            .addCase(fetchPlans.rejected, (state) => {
                state.availablePlans.isSuccess = false;
                state.availablePlans.isLoading = false;
                state.availablePlans.isError = true;
                state.availablePlans = initialState.availablePlans;
            })
            .addCase(choosePlan.pending, (state) => {
                state.selectedPlan.isLoading = true;
                state.selectedPlan.isSuccess = false;
                state.selectedPlan.isError = false;
            })
            .addCase(choosePlan.fulfilled, (state, action) => {
                state.selectedPlan.isSuccess = true;
                state.selectedPlan.isLoading = false;
                state.selectedPlan.plan = action.payload.plan;
                state.availablePlans.plans = action.payload.availablePlans;
            })
            .addCase(choosePlan.rejected, (state) => {
                state.selectedPlan.isSuccess = false;
                state.selectedPlan.isLoading = false;
                state.selectedPlan.isError = true;
                state.selectedPlan.plan = null;
            })
            .addCase(computeVat.pending, (state) => {
                state.vat.isLoading = true;
                state.vat.isSuccess = false;
                state.vat.isError = false;
            })
            .addCase(computeVat.fulfilled, (state, action) => {
                state.vat.isLoading = false;
                state.vat.isSuccess = true;
                state.vat.vat = action.payload.vat as ComputeVatOutput['vat'];
            })
            .addCase(computeVat.rejected, (state) => {
                state.vat.isSuccess = false;
                state.vat.isLoading = false;
                state.vat.isError = true;
            })
            .addCase(verifyChallenge.pending, (state) => {
                state.captcha.isLoading = true;
                state.captcha.isSuccess = false;
                state.captcha.isError = false;
            })
            .addCase(verifyChallenge.fulfilled, (state, action) => {
                state.captcha.isLoading = false;
                state.captcha.isSuccess = true;
                state.captcha.challenge = action.payload.challenge;
            })
            .addCase(verifyChallenge.rejected, (state) => {
                state.captcha.isSuccess = false;
                state.captcha.isLoading = false;
                state.captcha.isError = true;
            })
    }
});


export const { resetPlanUpgrade, resetVat } = planUpgradeSlice.actions;
export const planUpgradeState = (state: RootState) => state.planUpgrade;

export default planUpgradeSlice.reducer;